import dayjs from 'dayjs'
import { useDataActions } from 'hooks'
import { PATIENT_MEDICAL_EVENTS } from 'navigation/CONSTANTS'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useToast } from 'services/context/toast'
import { getMedicalEventList } from 'services/medical_event'
import {
  PATIENT_MEDICAL_EVENT_BASE,
  PATIENT_MEDICAL_EVENT_REGISTRATION_FILTERED
} from 'services/medical_event/CONSTANTS'
import { IMedicalEventResponse, IPostPatientMedicalEvent } from 'types'

const getToastMessage = (isEdition: boolean = false) => {
  if (isEdition) {
    return {
      success: 'Evento editado com sucesso',
      error: ['Houve um erro ao tentar editar o evento']
    }
  }

  return {
    success: 'Evento adicionado com sucesso',
    error: ['Houve um erro ao tentar adicionar o evento', 'Evento já existe']
  }
}

const fieldsNotNull: Array<keyof IPostPatientMedicalEvent> = [
  'medical_event_id',
  'event_date',
  'clavien_dindo'
]

export const usePatientMedicalEventAction = () => {
  const { patientId, medicalEventId } = useParams()
  const navigate = useNavigate()
  const toast = useToast()

  const [medicalEventList, setMedicalEventList] = useState<
    IMedicalEventResponse[]
  >([])
  const {
    sendRequest,
    state,
    errorsMap,
    setState,
    setErrorsMap,
    resetInputError,
    setData,
    data,
    ...hooks
  } = useDataActions<IPostPatientMedicalEvent>({
    patchApi: () =>
      PATIENT_MEDICAL_EVENT_REGISTRATION_FILTERED(medicalEventId ?? ''),
    postApi: () => PATIENT_MEDICAL_EVENT_BASE,
    defaultValue: {
      patient_id: patientId ?? '',
      medical_event_id: '',
      event_date: new Date().toISOString(),
      description: '',
      clavien_dindo: ''
    },
    formatData: (originData): IPostPatientMedicalEvent => {
      const formatEditMode = (
        originData: IPostPatientMedicalEvent,
        data: IPostPatientMedicalEvent
      ) => {
        const result: IPostPatientMedicalEvent = {} as IPostPatientMedicalEvent
        Object.keys(data).forEach(index => {
          const key = index as keyof IPostPatientMedicalEvent
          const origin = originData[key]
          const value = data[key]
          if (origin != value) {
            result[key] = (
              typeof value === 'string' ? value?.toString().trim() : value
            ) as never
          }
        })
        return result
      }

      const formatNonEditMode = (data: IPostPatientMedicalEvent) => {
        const result: IPostPatientMedicalEvent = {} as IPostPatientMedicalEvent
        Object.keys(data).forEach(index => {
          const key = index as keyof IPostPatientMedicalEvent
          const value = data[key]
          const current = typeof value === 'string' ? value.trim() : value
          result[key] = (current === '' ? undefined : current) as never
        })
        return result
      }

      return hooks.editMode
        ? formatEditMode(originData, data)
        : formatNonEditMode(data)
    },
    fieldsNotNull
  })

  type ErrorMap = typeof errorsMap

  const findOption = medicalEventList.find(
    item => item.medical_event_id === data.medical_event_id
  )

  const eventList = findOption
    ? medicalEventList.map(event => ({
        label: event.event_name,
        value: event.medical_event_id
      }))
    : [
        ...medicalEventList.map(event => ({
          label: event.event_name,
          value: event.medical_event_id
        })),
        { label: (data as any).event_name, value: data.medical_event_id }
      ]

  const validateForm = (): ErrorMap => {
    const newErrorsMap: ErrorMap = new Map()

    const { medical_event_id, event_date } = data

    if (medical_event_id == '') {
      newErrorsMap.set('medical_event_id', 'Selecione um evento')
    }

    if (!event_date) {
      newErrorsMap.set('event_date', 'Selecione uma data')
    } else if (dayjs(event_date).isAfter(dayjs())) {
      newErrorsMap.set('event_date', 'A data do evento não pode ser no futuro')
    }

    if (!data.clavien_dindo) {
      newErrorsMap.set(
        'clavien_dindo',
        'Selecione uma classificação Clavien-Dindo'
      )
    }

    errorsMap.clear()

    setErrorsMap(newErrorsMap)

    return newErrorsMap
  }

  const onSubmit = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault()
    event.stopPropagation()

    const errors = validateForm()
    if (state == 'loading' || errors.size > 0) return

    const messages = getToastMessage(hooks.editMode)

    try {
      await sendRequest()

      setState('success')
      toast.handler({
        content: messages['success']
      })
      navigate(`/${PATIENT_MEDICAL_EVENTS(patientId)}`, {
        replace: true
      })
    } catch (e) {
      setState('error')
      let error: number
      if (e instanceof Error) {
        error = e.message == 'duplicate' ? 1 : 0
      } else {
        error = 0
      }
      toast.handler({
        content: messages['error'][error],
        severity: 'error'
      })
    }
  }

  useEffect(() => {
    const fetchMedicalEvent = async () => {
      setState('loading')
      try {
        const response = await getMedicalEventList()

        if (response.success) {
          setState('success')
          setMedicalEventList(response.data || [])
        } else {
          setState('error')
        }
      } catch (e) {
        console.error(e)
        setState('error')
      }
    }

    fetchMedicalEvent()
  }, [])

  return {
    ...hooks,
    onSubmit,
    errorsMap,
    state,
    disableFields: state == 'loading',
    data,
    medicalEventList: eventList
  }
}
